import type { UseInfiniteQueryHandler, UseInfiniteQueryOptions, UseInfiniteQueryPage, UseInfiniteQueryReturn, UseQueryFunctionOptions } from '../../types'
import type { Merge } from 'type-fest'
import { type ApiError, type OpenapiError, computed, getCurrentInstance, getCurrentScope, onScopeDispose, onUnmounted, ref, shallowRef, toValue, useNuxtApp, useQuery, useQueryCache, watch } from '#imports'
import { omit } from 'es-toolkit'
import { isPrefetching } from '../../utils/prefetch'

export function useInfiniteQuery<TResponse, TError = ApiError<TResponse | OpenapiError<TResponse> | Error | null>>(handler: UseInfiniteQueryHandler<TResponse>, queryOptions: UseInfiniteQueryOptions<TResponse>): UseInfiniteQueryReturn<TResponse, TError> {
    const { getNextPageParam, getPreviousPageParam, initialPageParam, key, ...options } = queryOptions
    const cache = useQueryCache<Record<number, TResponse>>()

    function convertPageCacheToList(pageCache: Record<number, TResponse>) {
        return [...Object.entries(pageCache)].sort((a, b) => Number(a[0]) - Number(b[0])).map(pageTuple => ({
            pageParam: pageTuple[0],
            data: pageTuple[1]
        }))
    }

    function getPagesFromCache() {
        const pageCache = cache.get(toValue(key) as string)
        if (!pageCache) return []

        return convertPageCacheToList(pageCache)
    }

    const initialPages = getPagesFromCache()
    const pages = shallowRef<ReturnType<typeof getPagesFromCache>>(initialPages)
    const currentPage = shallowRef<UseInfiniteQueryPage<TResponse> | undefined>(
        initialPages.length ? initialPages[0] : undefined
    )

    // Client only: watchers created during SSR are never stopped, causing memory leaks
    if (import.meta.client) {
        const instance = getCurrentInstance()
        const scope = getCurrentScope()

        const stop = watch(() => toValue(key), () => {
            currentPage.value = undefined
        })

        if (instance) {
            onUnmounted(stop)
        }
        else if (scope) {
            onScopeDispose(stop)
        }
    }

    const allPages = computed(() => {
        if (!pages.value.length) return []

        return pages.value.map(page => page.data)
    })
    const firstPage = computed(() => {
        if (!allPages.value.length) return

        return allPages.value[0]
    })
    const lastPage = computed(() => {
        if (!allPages.value.length) return

        return allPages.value[allPages.value.length - 1]
    })

    const allPageParams = computed(() => {
        if (!pages.value.length) return []

        return pages.value.map(page => page.pageParam)
    })
    const firstPageParam = computed(() => {
        if (!allPageParams.value.length) return

        return allPageParams.value[0]
    })
    const lastPageParam = computed(() => {
        if (!allPageParams.value.length) return

        return allPageParams.value[allPageParams.value.length - 1]
    })
    const hasNextPage = computed(() => {
        if (!lastPage.value) return false

        return !!getNextPageParam({
            lastPage: lastPage.value,
            allPages: allPages.value,
            lastPageParam: lastPageParam.value,
            allPageParams: allPageParams.value,
            currentPage: currentPage.value
        })
    })
    const hasPreviousPage = computed(() => {
        if (!firstPage.value) return false
        if (!getPreviousPageParam) return false

        return !!getPreviousPageParam({
            firstPage: firstPage.value,
            allPages: allPages.value,
            firstPageParam: firstPageParam.value,
            allPageParams: allPageParams.value,
            currentPage: currentPage.value
        })
    })

    const direction = ref<'previous' | 'next'>('next')
    const isFetchingPreviousPage = computed(() => {
        if (direction.value !== 'previous') return false

        return query.status.value === 'pending'
    })
    const isFetchingNextPage = computed(() => {
        if (direction.value !== 'next') return false

        return query.status.value === 'pending'
    })

    const query = useQuery<Record<number, TResponse>>(async (ctx) => {
        let pageParam: number | undefined = undefined

        if (direction.value === 'next') {
            pageParam = lastPage.value
                ? getNextPageParam({
                        lastPage: lastPage.value,
                        allPages: allPages.value,
                        lastPageParam: lastPageParam.value,
                        allPageParams: allPageParams.value,
                        currentPage: currentPage.value
                    })
                : initialPageParam
        }

        if (getPreviousPageParam && direction.value === 'previous') {
            pageParam = firstPage.value
                ? getPreviousPageParam({
                        firstPage: firstPage.value,
                        allPages: allPages.value,
                        firstPageParam: firstPageParam.value,
                        allPageParams: allPageParams.value,
                        currentPage: currentPage.value
                    })
                : initialPageParam
        }

        // In case a pageParam cannot be resolved, set it to initialPageParam
        if (!pageParam) {
            pageParam = initialPageParam
        }

        const response = await handler({
            ...ctx,
            pageParam
        })

        // Build on existing pages rather than cache (which may be TTL-expired)
        const existingPages = pages.value.reduce((acc, page) => {
            acc[Number(page.pageParam)] = page.data
            return acc
        }, {} as Record<number, TResponse>)

        const data = { ...existingPages }

        data[pageParam!] = response

        pages.value = convertPageCacheToList(data)

        currentPage.value = {
            pageParam,
            data: response
        }

        return data as Record<number, TResponse>
    }, {
        ...(options as UseQueryFunctionOptions<Record<number, TResponse>>),
        // Not prefetched: the pages fill in the instance whose handler ran, so another instance's
        // request cannot be handed over.
        ...(isPrefetching(useNuxtApp()) && { enabled: false }),
        key,
    })

    // When getCachedDataHandler returns data, useAsyncData skips the handler entirely.
    // In that case, pages/currentPage are not populated by the handler, so we sync from query data.
    if (query.status.value === 'success' && !pages.value.length && query.data.value) {
        pages.value = convertPageCacheToList(query.data.value as Record<number, TResponse>)
        if (pages.value.length) {
            currentPage.value = pages.value[pages.value.length - 1]
        }
    }

    async function fetchNextPage() {
        // Don't fetch if already fetching another query
        if (pages.value.length && !hasNextPage.value) return
        if (direction.value === 'next' && query.status.value === 'pending') return

        direction.value = 'next'

        await query.refresh()

        return query
    }

    async function fetchPreviousPage() {
        // Don't fetch if already fetching another query
        if (!getPreviousPageParam) return
        if (!hasPreviousPage.value) return
        if (direction.value === 'previous' && query.status.value === 'pending') return

        direction.value = 'previous'

        await query.refresh()

        return query
    }

    const isLoading = query.isLoading

    const data = computed(() => {
        return {
            pages: pages.value,
            pageParams: allPageParams.value
        }
    })
    const error = computed(() => query.error.value)

    const _query = query.enrich({
        data,
        error,
        currentPage,
        fetchNextPage,
        fetchPreviousPage,
        isFetchingPreviousPage,
        isFetchingNextPage,
        hasNextPage,
        hasPreviousPage,
    })

    const _queryData = {
        ...omit(_query as any, ['then', 'catch', 'finally']),
        isLoading,
        enrich,
        bind,
    }

    function enrich<TProperties extends Record<string, any>>(properties: TProperties): Merge<typeof _queryData, TProperties> {
        const plain = {
            ..._queryData,
            ...properties,
        }

        return {
            ...plain,
            then(onfulfilled?: any, onrejected?: any) {
                return query.then(() => plain).then(onfulfilled, onrejected)
            },
            catch(onrejected?: any) {
                return query.then(() => plain).catch(onrejected)
            },
            finally(onfinally?: any) {
                return query.then(() => plain).finally(onfinally)
            },
        } as unknown as Merge<typeof _queryData, TProperties>
    }

    function bind<TProperties extends Record<string, any>>(properties: TProperties): TProperties & Promise<TProperties> {
        const plain = { ...properties }

        return {
            ...plain,
            then(onfulfilled?: any, onrejected?: any) {
                return query.then(() => plain).then(onfulfilled, onrejected)
            },
            catch(onrejected?: any) {
                return query.then(() => plain).catch(onrejected)
            },
            finally(onfinally?: any) {
                return query.then(() => plain).finally(onfinally)
            },
        } as unknown as TProperties & Promise<TProperties>
    }

    return {
        ..._queryData,
        then(onfulfilled?: any, onrejected?: any) {
            return query.then(() => _queryData).then(onfulfilled, onrejected)
        },
        catch(onrejected?: any) {
            return query.then(() => _queryData).catch(onrejected)
        },
        finally(onfinally?: any) {
            return query.then(() => _queryData).finally(onfinally)
        },
    } as unknown as UseInfiniteQueryReturn<TResponse, TError>
}
